import { useCallback, useEffect, useRef, useState } from "react";
import { ListChecks, Pencil, Trash2, Camera, CheckCircle2, Loader2, ImageOff, Eye, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { localPhotoStates, PHOTO_EVENT, retryPendingPhotos } from "@/lib/coPhoto";
import { PosVerdict } from "./PosVerdict";
import { RecordDelete } from "./RecordDelete";
import { CO_TIMEOUT_MS, coMessage, fmt, fmtSar, fmtTime, labelOf, PHOTO_STATUS_KEY, shiftDate } from "./coUtils";

const C = OPS.co;

const PHOTO_ICON = { stored: CheckCircle2, pending: Loader2, uploading: Loader2, failed: ImageOff, missing: ImageOff,
    lost: ImageOff };
const PHOTO_TONE = {
    stored: "text-[color:var(--success,#15803d)]",
    pending: "text-[color:var(--ink-muted)]",
    uploading: "text-[color:var(--ink-muted)]",
    failed: "text-[color:var(--danger)]",
    missing: "text-[color:var(--danger)]",
    lost: "text-[color:var(--danger)]",
};

/** What the row says about its photo: the server's status, except that a
 *  pending / failed photo whose bytes this device no longer holds is «الصورة
 *  لم تُرفع» (`lost`) — not a wait chip for an upload that will never come —
 *  and one whose PUT got its 2xx in this tab after the list was read is
 *  stored (the next read brings «عرض الصورة»). */
function shownPhotoStatus(rec, local) {
    const status = rec.photo_status || "missing";
    if (status !== "pending" && status !== "failed") return status;
    if (local === "uploading") return "uploading";
    if (local === "parked") return "pending";
    if (local === "stored") return "stored";
    return "lost";
}

/**
 * «إدخالاتي» — the caller's own records with `photo_status`, edit and
 * delete inside the window (`editable` is the server's verdict). `GET
 * /records` forces `created_by = self` for an `own` scope, so the isolation
 * is server-side. Parked photo blobs are retried every time this opens.
 * A master's delete always carries a reason (message 36, 422 without one),
 * so its trash icon opens the same reason dialog as the record panel.
 * Incident 2026-10-21: a cashier (scope `own`) has no «السجلات», so «عرض
 * الصورة» shows the stored photo here; a photo that never arrived and is not
 * on this device reads «الصورة لم تُرفع», with «إعادة التصوير» while editable.
 */
export function MyEntries({ scope, range, reloadKey, onEdit, onChanged, editingId }) {
    const { t, lang } = useI18n();
    const userId = useAuth()?.me?.user?.id || null;
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [delRec, setDelRec] = useState(null);
    const [viewRec, setViewRec] = useState(null);
    // {record_id: "uploading" | "parked" | "stored"}: the photos this device is still sending
    const [local, setLocal] = useState({});
    // only the latest read lands: a PUT's 2xx starts a second read while the first may still be out
    const loadSeq = useRef(0);
    const localSeq = useRef(0);
    const refreshLocal = useCallback(async () => {
        const n = ++localSeq.current;
        const states = await localPhotoStates();
        if (n === localSeq.current) setLocal(states);
    }, []);
    const isMaster = !!scope?.caps?.is_master;
    const labels = scope?.labels || {};
    const today = scope?.business_date;

    const load = useCallback(async () => {
        if (!today) return;
        const n = ++loadSeq.current;
        const days = Math.max(1, Math.ceil((scope?.settings?.backdate_hours || 48) / 24));
        try {
            const res = await api.get("/co/records", {
                params: { mine: 1, show_duplicates: 1, limit: 100, date_to: today,
                          date_from: range === "today" ? today : shiftDate(today, -days) },
            });
            const ln = ++localSeq.current;
            const states = await localPhotoStates();
            if (n !== loadSeq.current) return;   // a newer read is out (or landed): this answer is stale
            if (ln === localSeq.current) setLocal(states);
            setRows(res.data.items || []);
        } catch (e) {
            if (n !== loadSeq.current) return;
            const msg = coMessage(e, lang);
            if (msg) toast.error(msg);
        } finally {
            if (n === loadSeq.current) setLoading(false);
        }
    }, [today, range, scope?.settings?.backdate_hours, lang]);

    useEffect(() => {
        load().then(() => retryPendingPhotos((key, res) => {
            if (res === "rejected") toast(t("co_photo_rejected_notice"), { id: `co-photo-${key}` });
        }, { userId }));
    }, [load, reloadKey, t, userId]);

    // a PUT of this tab (a parked retry, or the in-memory upload after a save) ended or moved on
    useEffect(() => {
        const on = (e) => {
            const st = e?.detail?.state;
            if (st === "stored" || st === "failed") load();
            else refreshLocal();
        };
        window.addEventListener(PHOTO_EVENT, on);
        return () => window.removeEventListener(PHOTO_EVENT, on);
    }, [load, refreshLocal]);

    // live_background (17 §2.4): while a row still awaits its verdict, re-read
    // every minute so the chip arrives without a manual refresh.
    const effective = scope?.pos_lookup_effective || {};
    const awaiting = rows.some((r) => effective[r.branch_id] === "live_background" && r.pos
        && (r.pos.match === "unavailable" || r.pos.match === "not_found") && r.pos.anomaly === "none");
    useEffect(() => {
        if (!awaiting) return undefined;
        const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, 60000);
        return () => clearInterval(id);
    }, [awaiting, load]);

    const remove = async (rec) => {
        // the master: «سبب الحذف» dialog; everyone else: the confirm-only delete inside the window
        if (isMaster) { setDelRec(rec); return; }
        if (!window.confirm(t("co_delete_confirm"))) return;
        try {
            await api.delete(`/co/records/${rec.id}`, { timeout: CO_TIMEOUT_MS });
            toast.success(t("co_deleted"));
            onChanged && onChanged();
        } catch (e) {
            toast.error(coMessage(e, lang) || t("co_offline_error"));
        }
    };

    const totalSar = rows.reduce((s, r) => s + (Number(r.value_sar) || 0), 0);
    const sar = lang === "ar" ? "ر.س" : "SAR";

    return (
        <section data-testid={C.mine.list}
                 className="rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
            <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <ListChecks className="h-4 w-4 text-[color:var(--brand-brown)]" />
                <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("co_my_entries")}</h3>
                <span className="text-[11px] text-[color:var(--ink-faint)]">
                    {t(range === "today" ? "co_my_entries_hint" : "co_my_entries_window")}
                </span>
            </header>
            {loading ? <InlineLoader /> : rows.length ? (
                <>
                    <p data-testid={C.mine.total} className="mt-2 text-xs font-semibold text-[color:var(--ink-muted)]">
                        {fmt(t("co_my_entries_total"), { n: new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(rows.length), value: fmtSar(totalSar, lang, { halalas: false }) })}
                    </p>
                    <ul className="mt-2 divide-y divide-[color:var(--border-soft)]">
                        {rows.map((r) => (
                            <MyEntryRow key={r.id} rec={r} labels={labels} lang={lang} t={t} sar={sar}
                                        posMode={(scope?.pos_lookup_effective || {})[r.branch_id] || "off"}
                                        minDigits={scope?.settings?.pos_min_suffix_digits} local={local[r.id]}
                                        active={editingId === r.id} onEdit={onEdit} onDelete={remove}
                                        onView={setViewRec} />
                        ))}
                    </ul>
                </>
            ) : (
                <p data-testid={C.mine.empty} className="py-5 text-center text-sm text-[color:var(--ink-muted)]">
                    {t("co_my_entries_empty")}
                </p>
            )}
            {isMaster && delRec ? (
                <RecordDelete key={delRec.id} rec={delRec} scope={scope} open
                              onOpenChange={(o) => { if (!o) setDelRec(null); }}
                              onDeleted={() => { onChanged && onChanged(); }} />
            ) : null}
            {viewRec ? <PhotoViewer key={viewRec.id} rec={viewRec} t={t} onClose={() => setViewRec(null)} /> : null}
        </section>
    );
}

/** «عرض الصورة»: the stored photo of one's own record — the same signed URL
 *  (300 s) as the record panel (`GET /co/records/{id}/photo?json=1`). */
function PhotoViewer({ rec, t, onClose }) {
    const [url, setUrl] = useState(null);
    const [err, setErr] = useState(false);
    useEffect(() => {
        let alive = true;
        api.get(`/co/records/${rec.id}/photo`, { params: { json: 1 }, timeout: CO_TIMEOUT_MS })
            .then((r) => { if (alive) setUrl(r.data.url); })
            .catch(() => { if (alive) setErr(true); });
        return () => { alive = false; };
    }, [rec.id]);
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid={C.mine.photoDialog} className="bg-[color:var(--bg-surface)] sm:max-w-lg">
                <DialogHeader className="text-start sm:text-start">
                    <DialogTitle>{t("co_rec_photo_title")} <span dir="ltr">#{rec.invoice_no}</span></DialogTitle>
                    <DialogDescription className="sr-only">{t("co_rec_photo_title")}</DialogDescription>
                </DialogHeader>
                {err ? (
                    <p data-testid={C.mine.photoError} role="alert"
                       className="flex items-center gap-1.5 rounded-[var(--radius-md)] border border-dashed border-[color:var(--border-soft)] px-3 py-4 text-xs font-semibold text-[color:var(--ink-muted)]">
                        <ImageOff className="h-4 w-4 shrink-0" /><span>{t("co_photo_view_failed")}</span>
                    </p>
                ) : url ? (
                    <img data-testid={C.mine.photoImg} src={url} alt="" onError={() => setErr(true)}
                         className="max-h-[70vh] w-full rounded-[var(--radius-md)] bg-[color:var(--bg-surface-2)] object-contain" />
                ) : <InlineLoader />}
            </DialogContent>
        </Dialog>
    );
}

function MyEntryRow({ rec, labels, lang, t, sar, active, onEdit, onDelete, onView, posMode, minDigits, local }) {
    const status = shownPhotoStatus(rec, local);
    const Icon = PHOTO_ICON[status] || Camera;
    const phone = rec.phone_kind === "mobile" ? rec.phone_masked : t(`co_phone_kind_${rec.phone_kind}`);
    const badges = [
        rec.duplicate_of ? ["duplicate", t("co_badge_duplicate")] : null,
        rec.review?.status === "pending" || rec.review?.status === "needs_follow_up" ? ["review", t("co_badge_review")] : null,
        rec.backdated ? ["backdated", t("co_badge_backdated")] : null,
    ].filter(Boolean);

    return (
        <li data-testid={C.mine.row(rec.id)} data-active={active ? "true" : "false"}
            className={`flex items-start justify-between gap-2 py-2.5 ${active ? "bg-[color:var(--brand-orange-soft)] -mx-3 px-3" : ""}`}>
            <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-baseline gap-2 text-sm font-bold text-[color:var(--ink)]">
                    <span dir="ltr">#{rec.invoice_no}</span>
                    <span dir="ltr">{fmtSar(rec.value_sar, lang, { halalas: false })} {sar}</span>
                    <span className="text-[11px] font-semibold text-[color:var(--ink-faint)]">{fmtTime(rec.occurred_at, lang)}</span>
                </div>
                <p className="truncate text-[11px] text-[color:var(--ink-muted)]">
                    {labelOf(labels.outcomes, rec.outcome, lang)} · {labelOf(labels.categories, rec.category, lang)}
                    {phone ? <span>{` · ${phone}`}</span> : null}
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                    {/* data-status = the server's photo_status; data-shown = what the row says. The icon
                        changes with the status: the label has its own element (a translated page — PhotoField) */}
                    <span data-testid={C.mine.photoStatus(rec.id)} data-status={rec.photo_status || "missing"}
                          data-shown={status} translate="no"
                          className={`inline-flex items-center gap-1 text-[11px] font-semibold ${PHOTO_TONE[status] || ""}`}>
                        <Icon className={`h-3.5 w-3.5 ${status === "pending" || status === "uploading" ? "animate-spin" : ""}`} />
                        <span>{t(PHOTO_STATUS_KEY[status] || "co_photo_failed")}</span>
                    </span>
                    {rec.has_photo ? (
                        <button type="button" data-testid={C.mine.viewPhoto(rec.id)} onClick={() => onView(rec)}
                                className="inline-flex h-7 items-center gap-1 rounded-full border border-[color:var(--border-soft)] px-2.5 text-[11px] font-semibold text-[color:var(--brand-brown)] hover:bg-[color:var(--brand-orange-soft)]">
                            <Eye className="h-3.5 w-3.5" /><span>{t("co_photo_view")}</span>
                        </button>
                    ) : null}
                    {status === "lost" && rec.editable ? (
                        <button type="button" data-testid={C.mine.retake(rec.id)} onClick={() => onEdit(rec)}
                                className="inline-flex h-7 items-center gap-1 rounded-full border border-[color:var(--danger)]/40 px-2.5 text-[11px] font-semibold text-[color:var(--danger)] hover:bg-[color:var(--danger-soft,rgba(220,38,38,0.08))]">
                            <RefreshCw className="h-3.5 w-3.5" /><span>{t("co_photo_retake")}</span>
                        </button>
                    ) : null}
                    {badges.map(([k, label]) => (
                        <span key={k} data-badge={k}
                              className="rounded-full bg-[color:var(--bg-surface-2)] px-2 py-0.5 text-[10px] font-semibold text-[color:var(--ink-muted)]">
                            {label}
                        </span>
                    ))}
                    {posMode !== "off" && rec.pos?.match ? (
                        <PosVerdict v={rec.pos} mode={posMode} minDigits={minDigits} t={t} lang={lang}
                                    testId={C.mine.posStatus(rec.id)} compact />
                    ) : null}
                </div>
            </div>
            {rec.editable ? (
                <div className="flex shrink-0 gap-1">
                    <button type="button" data-testid={C.mine.edit(rec.id)} onClick={() => onEdit(rec)} aria-label={t("co_edit")}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--border-soft)] text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)]">
                        <Pencil className="h-4 w-4" />
                    </button>
                    <button type="button" data-testid={C.mine.del(rec.id)} onClick={() => onDelete(rec)} aria-label={t("co_delete")}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--border-soft)] text-[color:var(--danger)] hover:bg-[color:var(--danger-soft,rgba(220,38,38,0.08))]">
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            ) : null}
        </li>
    );
}
